import { useCallback } from "react";
import { useSetAtom } from "jotai";
import { useApi } from "./useApi";
import { buildQueryString } from "@utils/queryString";
import { useSessionSetAtom } from "../useSessionSetAtom";
import { createReadGuard } from "./_helpers/readGuard";
import {
    FEE_SAVE_FAILED,
    ORDER_SAVE_FAILED,
    OrderStatusType,
    stockShortfallItems,
    type AdminOrderDetailResponse,
    type ChargeReturnedPaymentFeeRequest,
    type ChargeReturnedPaymentFeeResponse,
    type CreateOrderRequest,
    type CreateOrderResponse,
    type GetMeOrderResponse,
    type GetMeOrdersResponse,
    type GetOrdersQuery,
    type OrderStatusCounts,
    type PaginatedOrders,
    type RecordPaymentReturnResponse,
    type TransitionOrderRequest,
} from "@/models/order";
import {
    PAYMENT_DECLINED,
    PAYMENT_HELD,
    PAYMENT_UNAVAILABLE,
    PAYMENT_UNCONFIRMED,
} from "@models/paymentMethod";
import {
    isProcessingOrderAtom,
    isFetchingMeOrdersAtom,
    meOrdersFetchErrorAtom,
    ordersListAtom,
    isFetchingOrdersListAtom,
    orderStatusCountsAtom,
    adminOrderDetailAtom,
    isFetchingAdminOrderAtom,
} from "@/store/orderAtoms";

const ORDERS_ENDPOINT = "/orders";

// Which admin list / detail read may publish to its atoms (PR 369 review 1-2).
// Module-scope, like useProductApi's: the atoms are module-scope (the app
// mounts no Jotai Provider), so only a sequence answers "has a newer read
// taken over?". Without it a slower answer for order A, or for the previous
// status filter, lands after the newer one and replaces it. Latest-only
// (`isLatest`).
const orderListReads = createReadGuard();
const adminOrderReads = createReadGuard();

// Checkout renders these contracted POST /orders failure outcomes INLINE, so
// useApi's toast is suppressed for exactly them: the four payment outcomes
// (#156) and payment_unavailable (MB-05 — the toast would say "Payment
// declined" for a card that was never judged), both 409s — the cart claim's plain string (#441) and the coded
// stock_changed_during_checkout (PR 339), told apart by checkoutOrderError — and the stock 412 (#443). Everything
// else — problem+json 400s, FluentValidation, every other 412, unknown 402
// codes, body-less 5xx — keeps useApi's bespoke handlers, and a network
// failure (no status) is none of these, so it keeps the network-error toast.
const suppressInlineOrderFailure = (
    status: number | undefined,
    errorCode: string | undefined,
    _hasApiBody: boolean,
    bodyText: string,
): boolean =>
    (status === 402 &&
        (errorCode === PAYMENT_DECLINED ||
            errorCode === PAYMENT_UNCONFIRMED ||
            errorCode === PAYMENT_HELD ||
            errorCode === PAYMENT_UNAVAILABLE)) ||
    (status === 500 && errorCode === ORDER_SAVE_FAILED) ||
    status === 409 ||
    (status === 412 && stockShortfallItems(bodyText) !== undefined);

// The admin returned-payment-fee dialog (#298) renders its contracted failures
// INLINE: the four 402 payment codes (payment_unavailable too: nothing was
// charged, and useApi's 402 toast would title it "Payment declined", blaming
// the card), every 409 (each refusal has its own code since PR 358, but the
// status alone identifies them, so no code check), and the 500
// fee_save_failed — charged but not saved (C-09), which useApi's generic
// server-error toast would invite the admin to retry. Everything else keeps
// useApi's bespoke handlers, and a network failure (no status) keeps the
// network-error toast.
const suppressInlineFeeFailure = (
    status: number | undefined,
    errorCode?: string,
): boolean =>
    status === 409 ||
    (status === 402 &&
        (errorCode === PAYMENT_DECLINED ||
            errorCode === PAYMENT_UNCONFIRMED ||
            errorCode === PAYMENT_HELD ||
            errorCode === PAYMENT_UNAVAILABLE)) ||
    (status === 500 && errorCode === FEE_SAVE_FAILED);

export const useOrderApi = () => {
    const { callApi } = useApi();
    // Session-bound setters for what endSessionAtom clears; the in-flight
    // flags keep plain setters so their `finally` always lands.
    const setIsProcessing = useSetAtom(isProcessingOrderAtom);
    const setIsFetchingMeOrders = useSetAtom(isFetchingMeOrdersAtom);
    const setMeOrdersFetchError = useSessionSetAtom(meOrdersFetchErrorAtom);
    const setOrdersList = useSessionSetAtom(ordersListAtom);
    const setIsFetchingOrdersList = useSetAtom(isFetchingOrdersListAtom);
    const setStatusCounts = useSessionSetAtom(orderStatusCountsAtom);
    const setAdminOrderDetail = useSessionSetAtom(adminOrderDetailAtom);
    const setIsFetchingAdminOrder = useSetAtom(isFetchingAdminOrderAtom);

    // The API binds the request body to a `Body`-named param, so the whole JSON
    // body is the command body — i.e. the CreateOrderRequest itself.
    const createOrder = useCallback(
        async (request: CreateOrderRequest): Promise<CreateOrderResponse> => {
            setIsProcessing(true);

            try {
                return await callApi<CreateOrderResponse>(ORDERS_ENDPOINT, {
                    method: "POST",
                    body: JSON.stringify(request),
                    skipErrorNotification: suppressInlineOrderFailure,
                });
            } finally {
                setIsProcessing(false);
            }
        },
        [callApi, setIsProcessing],
    );

    // The order confirmation page says "Order not found" for a 404, so only
    // that skips useApi's toast; every other failure keeps it beside the
    // page's Retry.
    const getMeOrder = useCallback(
        (orderId: string): Promise<GetMeOrderResponse> =>
            callApi<GetMeOrderResponse>(`${ORDERS_ENDPOINT}/me/${orderId}`, {
                skipErrorNotification: (status) => status === 404,
            }),
        [callApi],
    );

    const getMeOrders = useCallback(async (): Promise<
        GetMeOrdersResponse[]
    > => {
        setIsFetchingMeOrders(true);
        setMeOrdersFetchError(false);

        try {
            return await callApi<GetMeOrdersResponse[]>(
                `${ORDERS_ENDPOINT}/me`,
            );
        } catch (error) {
            setMeOrdersFetchError(true);
            throw error;
        } finally {
            setIsFetchingMeOrders(false);
        }
    }, [callApi, setIsFetchingMeOrders, setMeOrdersFetchError]);

    // Admin orders list (E6-S9 / US 206). GET /orders — paginated.
    const getOrderList = useCallback(
        async (query: GetOrdersQuery): Promise<PaginatedOrders> => {
            const { isLatest } = orderListReads.begin();

            setIsFetchingOrdersList(true);

            try {
                const queryString = buildQueryString(query);
                const url = queryString
                    ? `${ORDERS_ENDPOINT}?${queryString}`
                    : ORDERS_ENDPOINT;
                const response = await callApi<PaginatedOrders>(url);

                if (isLatest()) setOrdersList(response);

                return response;
            } finally {
                if (isLatest()) setIsFetchingOrdersList(false);
            }
        },
        [callApi, setOrdersList, setIsFetchingOrdersList],
    );

    const getOrderStatuses =
        useCallback(async (): Promise<OrderStatusCounts> => {
            const response = await callApi<OrderStatusCounts>(
                `${ORDERS_ENDPOINT}/statuses`,
            );
            setStatusCounts(response);
            return response;
        }, [callApi, setStatusCounts]);

    const getAdminOrder = useCallback(
        async (orderId: string): Promise<AdminOrderDetailResponse> => {
            const { isLatest } = adminOrderReads.begin();

            setIsFetchingAdminOrder(true);

            try {
                const data = await callApi<AdminOrderDetailResponse>(
                    `${ORDERS_ENDPOINT}/${orderId}`,
                );
                if (isLatest()) setAdminOrderDetail(data);

                return data;
            } finally {
                if (isLatest()) setIsFetchingAdminOrder(false);
            }
        },
        [callApi, setAdminOrderDetail, setIsFetchingAdminOrder],
    );

    const transitionOrder = useCallback(
        async (
            orderId: string,
            request: TransitionOrderRequest,
        ): Promise<void> => {
            setIsProcessing(true);

            try {
                await callApi(`${ORDERS_ENDPOINT}/${orderId}/transition`, {
                    method: "POST",
                    body: JSON.stringify(request),
                });
            } finally {
                setIsProcessing(false);
            }
        },
        [callApi, setIsProcessing],
    );

    // The Orders list's bulk Mark Processed (#473): transitionOrder's request
    // for one order. The page renders every order's outcome itself, so no
    // toast (one per order would bury it), and no shared isProcessing flag:
    // the page counts its own run.
    const markOrderProcessed = useCallback(
        async (orderId: string): Promise<void> => {
            const request: TransitionOrderRequest = {
                toStatus: OrderStatusType.Processing,
            };

            await callApi(`${ORDERS_ENDPOINT}/${orderId}/transition`, {
                method: "POST",
                body: JSON.stringify(request),
                skipErrorNotification: true,
            });
        },
        [callApi],
    );

    // Admin NSF fee (#298). POST /orders/{orderId}/returned-payment-fee —
    // charges the configured fee against the order's saved payment profile.
    const chargeReturnedPaymentFee = useCallback(
        async (
            orderId: string,
            request: ChargeReturnedPaymentFeeRequest,
        ): Promise<ChargeReturnedPaymentFeeResponse> => {
            setIsProcessing(true);

            try {
                return await callApi<ChargeReturnedPaymentFeeResponse>(
                    `${ORDERS_ENDPOINT}/${orderId}/returned-payment-fee`,
                    {
                        method: "POST",
                        body: JSON.stringify(request),
                        skipErrorNotification: suppressInlineFeeFailure,
                    },
                );
            } finally {
                setIsProcessing(false);
            }
        },
        [callApi, setIsProcessing],
    );

    // Admin "Record bank return" (C-10). POST /orders/{orderId}/payment-return,
    // no body. Its 409 refusals render inline in the confirm dialog, so only
    // those skip useApi's toast; a 404 or anything else keeps it.
    const recordPaymentReturn = useCallback(
        async (orderId: string): Promise<RecordPaymentReturnResponse> => {
            setIsProcessing(true);

            try {
                return await callApi<RecordPaymentReturnResponse>(
                    `${ORDERS_ENDPOINT}/${orderId}/payment-return`,
                    {
                        method: "POST",
                        skipErrorNotification: (status) => status === 409,
                    },
                );
            } finally {
                setIsProcessing(false);
            }
        },
        [callApi, setIsProcessing],
    );

    return {
        createOrder,
        getMeOrder,
        getMeOrders,
        getOrderList,
        getOrderStatuses,
        getAdminOrder,
        transitionOrder,
        markOrderProcessed,
        chargeReturnedPaymentFee,
        recordPaymentReturn,
    };
};
